import axios from 'axios'
import {
  FALLBACK_IMAGE,
  BASE_URL,
  buildFAQPageJsonLd,
  buildBreadcrumbJsonLd
} from '~/utils/seo/jsonLd'
/**
 * SSR 인메모리 FAQ 캐시 — /cs/csi/faqList 조회 결과를 서버 프로세스에 캐싱.
 * TTL 30분, in-flight dedup (thundering-herd 방지), stale-on-error.
 * 단일 키 'faqList' (파라미터 고정, 비개인화).
 */
const FAQ_CACHE_TTL_MS = 30 * 60 * 1000
const _cache = new Map()
const _inflight = new Map()

function fetchFaqList (apiBase) {
  const now = Date.now()
  const key = 'faqList'
  const hit = _cache.get(key)
  if (hit && (now - hit.ts) < FAQ_CACHE_TTL_MS) {
    return Promise.resolve(hit.data)
  }
  // Thundering-herd dedup: 동시 접속해도 동일 키는 1건만 API 호출
  let p = _inflight.get(key)
  if (!p) {
    p = axios.post(apiBase + '/cs/csi/faqList', {
      faqTyCd: 'all',
      searchType: 'init',
      currentPage: 1,
      pageSize: 10,
      searchInput: null
    })
      .then(res => {
        const data = (res && res.data) || null
        let faqItems = null
        if (data && data.success && data.data && data.data.length > 0) {
          const stripHtml = (html) => {
            if (!html) { return '' }
            let s = String(html)
            // HTML 엔티티 디코딩 (이중 인코딩 대응: 최대 2회)
            for (let i = 0; i < 2; i++) {
              s = s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
                .replace(/&#0?39;/g, "'").replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
            }
            // HTML 태그 제거 → 잔여 엔티티 제거 → 공백 정리
            return s.replace(/<[^>]+>/g, ' ').replace(/&[a-zA-Z#0-9]+;/g, ' ').replace(/\s+/g, ' ').trim()
          }
          faqItems = data.data
            .filter(item => item.faqSj && item.faqCn)
            .slice(0, 10)
            .map(item => ({
              question: item.faqSj,
              answer: stripHtml(item.faqCn)
            }))
        }
        _cache.set(key, { data: faqItems, ts: Date.now() })
        return faqItems
      })
      .catch(() => {
        // API 실패 시 기존 캐시 반환 (stale-on-error)
        const stale = _cache.get(key)
        return stale ? stale.data : null
      })
      .finally(() => { _inflight.delete(key) })
    _inflight.set(key, p)
  }
  return p
}
/**
 * FAQ 페이지 seoMeta 핸들러 (FO)
 * - /cs/csi/faqList API 1건 호출 (상위 10건)
 * - FAQPage + BreadcrumbList JSON-LD SSR 주입
 */
export default async function handleFaq ({ store }) {
  const canonicalUrl = `${BASE_URL}/cs/csi/SCR_CSI_0001`
  const description = '다이소몰 자주 묻는 질문(FAQ)입니다. 배송, 결제, 교환/반품, 회원 등 궁금한 사항을 확인하세요.'

  try {
    const faqItems = await fetchFaqList(process.env.API_BASE_URL)
    const jsonLdList = []

    if (faqItems && faqItems.length > 0) {
      const faqPageJsonLd = buildFAQPageJsonLd(faqItems)
      if (faqPageJsonLd) { jsonLdList.push(faqPageJsonLd) }
    }

    // BreadcrumbList
    const breadcrumb = buildBreadcrumbJsonLd([
      { name: '홈', url: BASE_URL },
      { name: '고객센터', url: `${BASE_URL}/cs/csh/SCR_CSH_0000` },
      { name: 'FAQ', url: canonicalUrl }
    ])
    jsonLdList.push(breadcrumb)

    store.commit('seoMeta/set', {
      title: 'FAQ - 다이소몰',
      description,
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      ogType: 'website',
      canonical: canonicalUrl,
      mobileUrl: 'https://m.daisomall.co.kr/cs/csb/SCR_CSB_0001',
      h1: 'FAQ',
      jsonLd: jsonLdList
    })
  } catch (e) {
    console.error('[seoMeta/faq]', e.message)
    store.commit('seoMeta/set', {
      title: 'FAQ - 다이소몰',
      description,
      ogUrl: canonicalUrl,
      ogImage: FALLBACK_IMAGE,
      canonical: canonicalUrl
    })
  }
}
